import React from 'react';
import type { PositionRiskWidgetInstanceConfig, PositionRiskData } from './types';
import { fetchPositionRisk } from './positionRiskService';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner } from '../../../design';
import { useWidgetData } from '../../../blocks/hooks';

// Sample data shown during dashboard construction (no asset assigned)
const SAMPLE_DATA: PositionRiskData = {
  uid: 'SAMPLE',
  bet: { positionMM: -10, varMM: 1.5, siPercentFloat: 2.0 },
  riskSize: { pctGMV: 4.0, pctEquity: 8.0, pctIdioVol: 9.0 },
  liquidity: { pctADTV: 0.5, pctADTVEPS: 0.1 },
};

interface PositionRiskWidgetRendererProps {
  config: PositionRiskWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

/** Format a number as currency in $MM */
const fmtMM = (v: number): string => {
  const abs = Math.abs(v);
  const formatted = `$${abs}`;
  return v < 0 ? `(${formatted})` : formatted;
};

const PositionRiskWidgetRenderer: React.FC<PositionRiskWidgetRendererProps> = ({
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useWidgetData<PositionRiskData>(
    () => fetchPositionRisk(uid!),
    [uid],
    { enabled: !!uid, widgetInstanceId },
  );

  if (loading) {
    return <WidgetLoadingState size="sm" />;
  }

  if (error) {
    return <WidgetErrorState message={error} onRetry={refetch} />;
  }

  const d = uid ? data : SAMPLE_DATA;
  if (!d) return null;

  return (
    <div className={`h-full overflow-auto bg-surface ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}
      <div className="p-3 space-y-3 text-sm">
        {/* Bet */}
        <div>
          <h4 className="font-bold text-neutral-900 underline mb-1">Bet</h4>
          <div className="space-y-0.5 text-neutral-700">
            <p>Position: {fmtMM(d.bet.positionMM)} M</p>
            <p>VaR: {fmtMM(d.bet.varMM)} M</p>
            <p>{d.bet.siPercentFloat}% SI % Float</p>
          </div>
        </div>

        {/* Risk Size */}
        <div>
          <h4 className="font-bold text-neutral-900 underline mb-1">Risk Size</h4>
          <div className="space-y-0.5 text-neutral-700">
            <p>{d.riskSize.pctGMV}% GMV</p>
            <p>{d.riskSize.pctEquity}% Equity</p>
            <p>{d.riskSize.pctIdioVol}% IdioVol</p>
          </div>
        </div>

        {/* Liquidity */}
        <div>
          <h4 className="font-bold text-neutral-900 underline mb-1">Liquidity</h4>
          <div className="space-y-0.5 text-neutral-700">
            <p>{d.liquidity.pctADTV}% ADTV</p>
            <p>{d.liquidity.pctADTVEPS}% ADTV +/-3 day EPS</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionRiskWidgetRenderer;
